import moment from 'moment'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { deserialize } from '../../../../../utility/serialization'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { COMMON_ERRORS } from '../../../../../misc/Constants'
import { DATETIME_FORMAT, getMomentInCommunityTimezone } from '../../../../../utility/datesHelper'
import { requestCommunityProgramsEnrollmentsActivityReport } from '../../../../../actions/reportsActions'
import Loader from '../../../../common/loader/Loader'
import Select from '../../../../common/form/Select'

import { buildCommunityYearRangeFunctions } from '../../communityYearRanges'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import classes from './ProgramsEnrollmentsActivityWidget.module.scss'
import ProgramsEnrollmentsLineChart from './ProgramsEnrollmentsLineChart'
import Placeholder, { EXCEPTION_CASE } from './Placeholder'

const SINGLE_STATISTICS_COLORS = {
  blue: 'blue',
}

const SingleStatistics = ({ data, topLabel, bottomLabel, bottomLabelColor, hasZeroInsteadOfDash, withGrowthLabel }) => {
  const growthValue = data.prevValue ? data.value - data.prevValue : null
  const useDash = data.value === null || data.value === 0 && !hasZeroInsteadOfDash

  return (
    <div className={classnames('d-flex flex-column text-center', businessDashboardClasses.statistics)}>
      <span className={businessDashboardClasses.topLabel}>{topLabel}</span>
      <span className={businessDashboardClasses.value}>
        {useDash ? '-' : data.value}
      </span>
      {withGrowthLabel ? (
        <span
          className={
            classnames(businessDashboardClasses.bottomLabel, growthValue < 0 ? businessDashboardClasses.red : businessDashboardClasses.green)
          }
        >
          {Intl.NumberFormat('en-US', { signDisplay: 'always' })
            .format(growthValue)} vs Last 30 Days
        </span>
      ) : (
        <span className={classnames(businessDashboardClasses.bottomLabel, { [businessDashboardClasses[bottomLabelColor]]: !!bottomLabelColor })}>
          {bottomLabel}
        </span>
      )}
    </div>
  )
}

SingleStatistics.propTypes = {
  data: PropTypes.shape({
    value: PropTypes.number,
    prevValue: PropTypes.number,
  }).isRequired,
  topLabel: PropTypes.string.isRequired,
  bottomLabel: PropTypes.string,
  bottomLabelColor: PropTypes.string,
  hasZeroInsteadOfDash: PropTypes.bool,
  withGrowthLabel: PropTypes.bool,
}

const ProgramsEnrollmentsActivityWidget = () => {
  const community = useSelector(state => state.user.current_user.communities[0])
  const communityYearRangeFunctions = useMemo(() => buildCommunityYearRangeFunctions(community?.created_at), [community?.created_at])
  const yearSelectOptions = useMemo(() => Object.keys(communityYearRangeFunctions).reverse()
    .map(key => ({
      label: communityYearRangeFunctions[key].textFunc(),
      value: key,
    })), [communityYearRangeFunctions])
  const defaultFilters = useMemo(() => ({
    yearOption: yearSelectOptions[0],
  }), [yearSelectOptions])

  const [filters, setFilters] = useState(defaultFilters)
  const [isLoadingChartData, setIsLoadingChartData] = useState(true)
  const [data, setData] = useState()
  const exceptionCase = useMemo(() => {
    if (data?.selectedRange?.statistics?.totalCourses.value === 0) {
      return EXCEPTION_CASE.NO_PROGRAMS
    } else if (data?.selectedRange?.monthlyData?.every(month => month.value === 0)) {
      return EXCEPTION_CASE.NO_ENROLLMENTS
    } else {
      return null
    }
  }, [data?.selectedRange?.monthlyData, data?.selectedRange?.statistics?.totalCourses.value])

  useEffect(() => {
    setIsLoadingChartData(true)

    const { startDate, endDate } = communityYearRangeFunctions[filters.yearOption.value].valueFunc()

    requestCommunityProgramsEnrollmentsActivityReport({
      communityId: community?.id,
      startDate: getMomentInCommunityTimezone(moment(startDate), community?.timezone, DATETIME_FORMAT)
        .toDate(),
      endDate: getMomentInCommunityTimezone(moment(endDate), community?.timezone, DATETIME_FORMAT)
        .toDate(),
    })
      .then(res => {
        setData(deserialize(res.data.report.data))
        setIsLoadingChartData(false)
      })
      .catch(() => toast.error(COMMON_ERRORS[500]))
  }, [community, communityYearRangeFunctions, filters.yearOption])

  const handleSelectYearOption = (yearOption) => {
    if (!yearOption) {
      return
    }

    setFilters(prevState => ({ ...prevState, yearOption }))
  }

  return (
    <section className={businessDashboardClasses.widget}>
      <div className={classnames('d-flex justify-content-between', businessDashboardClasses.titleWrapper)}>
        <h2 className={businessDashboardClasses.title}>Programs & Enrollments Activity</h2>
        <Select
          id='programsEnrollmentsYearSelect'
          classNames={{
            control: () => classes.yearSelectControl,
            singleValue: () => classes.yearSelectSingleValue,
            menuList: () => classes.yearSelectMenuList,
          }}
          options={yearSelectOptions}
          value={filters.yearOption}
          onChange={handleSelectYearOption}
          isSearchable={false}
        />
      </div>
      {isLoadingChartData ? (
        <div className='flex-grow-1 d-flex justify-content-center align-items-center'>
          <Loader>Loading data</Loader>
        </div>
      ) : (
        <>
          <div className='d-flex'>
            <div style={{ flexBasis: '55%' }} className='mr-4'>
              <h3 className={businessDashboardClasses.statisticsTitle}>Today&apos;s Activity (Live)</h3>
              <span className={businessDashboardClasses.statisticsSubtitle}>What&apos;s happening right now across your active programs</span>
              <div className={classnames('d-flex justify-content-between', businessDashboardClasses.sectionWrapper)}>
                <SingleStatistics
                  topLabel='Active Programs'
                  data={data.currentTime.statistics.runningCourses}
                  withGrowthLabel
                />
                <SingleStatistics
                  data={data.currentTime.statistics.runningEnrollments}
                  topLabel='Active Enrollments'
                  withGrowthLabel
                />
                <SingleStatistics
                  data={data.currentTime.statistics.spotsAvailable}
                  topLabel='Spots Available'
                  hasZeroInsteadOfDash
                  bottomLabel='Total Capacity'
                  bottomLabelColor={SINGLE_STATISTICS_COLORS.blue}
                />
                <SingleStatistics
                  data={data.currentTime.statistics.waitlistSize}
                  topLabel='Waitlisted'
                  bottomLabel='Golfers on Waitlist'
                  bottomLabelColor={SINGLE_STATISTICS_COLORS.blue}
                />
              </div>
            </div>
            <div className='flex-grow-1'>
              <h3 className={businessDashboardClasses.statisticsTitle}>Performance Trends</h3>
              <span className={businessDashboardClasses.statisticsSubtitle}>Use the filter to explore past activity and growth</span>
              <div className={classnames('d-flex justify-content-between', businessDashboardClasses.sectionWrapper)}>
                <SingleStatistics
                  data={data.selectedRange.statistics.totalCourses}
                  topLabel='Total Programs'
                  bottomLabel={`In ${filters.yearOption.value}`}
                />
                <SingleStatistics
                  data={data.selectedRange.statistics.completedCourses}
                  topLabel='Completed Programs'
                  bottomLabel={`In ${filters.yearOption.value}`}
                />
                <SingleStatistics
                  data={data.selectedRange.statistics.totalEnrollments}
                  topLabel='Total Enrollments'
                  bottomLabel={`In ${filters.yearOption.value}`}
                />
              </div>
            </div>
          </div>
          <div className={businessDashboardClasses.chartWrapper}>
            {exceptionCase ? (
              <Placeholder exceptionCase={exceptionCase} />
            ) : (
              <ProgramsEnrollmentsLineChart monthlyData={data.selectedRange.monthlyData} />
            )}
          </div>

        </>
      )}
    </section>
  )
}

export default ProgramsEnrollmentsActivityWidget
